import { Grid, useMediaQuery } from '@mui/material';
import React from 'react';
import {
  CaptionText,
  Heading,
  MinorHeading,
  Subtitle
} from '../../components/shared/typography/PrimaryTypography';
import PrimaryDivider from '../../components/shared/decoration/PrimaryDivider';
import PrimaryButton from '../../components/shared/button/PrimaryButton';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import CollapseCard from '../../components/shared/cards/CollapseCard';

interface FAQ {
  question: string;
  answer: string | React.ReactNode;
}

const faqs: Array<FAQ> = [
  {
    question: `How is Syntiro's subscription pricing structured?`,
    answer: (
      <>
        Syntiro platform subscription fees are based on the number of users, legal entity size and
        the number of metrics/meters configured.{' '}
        <a href="/contact">Contact the Energy Partners team</a> for more information on our pricing.
      </>
    )
  },
  {
    question: `Can I subscribe to specific modules only?`,
    answer: (
      <>
        Yes, clients can subscribe to any number of the Syntiro modules to align with their current
        business needs, and it is easy to upgrade to additional modules.
      </>
    )
  },
  {
    question: `How does Syntiro link to other parts of the EP business?`,
    answer: (
      <>
        Syntiro is the core platform across all EP divisions and seamlessly integrates with Energy
        Partners’ broader service offering, including metering, BMS and IoT, O&M, monitoring and
        control services, tariff projects and various consulting services.
      </>
    )
  },
  {
    question: `Does the subscription include training?`,
    answer: (
      <>
        Syntiro offers guiding tours and built-in tooltips to help the user navigate the platform
        with ease. The Resource Centre also includes helpful training videos on how to use the main
        platform features. Energy Partners does offer additional platform training, as well as
        general training on utilities, sustainability concepts and behavioural insights outside of
        the standard subscription offering.
      </>
    )
  },
  {
    question: `Do I need to have existing utility meters on site?`,
    answer: (
      <>
        If you already have existing utility metering on site, the Syntiro platform can integrate
        with any Automatic Meter Reading or data source. If not, our in-house metering team can
        provide a comprehensive metering solution.
        <br />
        <br />
        Alternatively, for sites without utility metering, you can manually upload information from
        your utility or fuel bills.
      </>
    )
  },
  {
    question: `Can Syntiro integrate with all my facility’s systems and communication protocols? `,
    answer: (
      <>
        Syntiro is measuring-device agnostic and can integrate with various Automatic Meter Readers
        and controllers in the field. The platform can also integrate with the ERP system, to
        collect utility data, as well as production, operational and health and safety information.
      </>
    )
  },
  {
    question: `What sets Syntiro apart from the other sustainability platforms out there? `,
    answer: (
      <>
        Syntiro is a unified platform designed to support everything related to your business’s
        energy, sustainability and ESG goals. Its architecture enables holistic data consolidation,
        giving you a single source of truth across your entire business – from technical plant data
        (operational level) and utility billing (management level), to carbon emissions (strategic
        level).
      </>
    )
  },
  {
    question: `I’m new to this and don’t know where to start – how will Syntiro help me?`,
    answer: (
      <>
        Syntiro is built to guide you through every stage of your sustainability journey. Your
        starting point is the ESG Readiness Gauge, a built-in assessment tool that uses your
        responses to a questionnaire to gauge your company’s ESG preparedness. Based on the results,
        Syntiro will provide recommendations to improve your sustainability strategy, identify
        potential initiatives that will help you ‘move the needle’ towards your goals, and enhance
        your reporting output to be audit ready.
      </>
    )
  },
  {
    question: `What is the purpose of the Readiness Gauge?`,
    answer: (
      <>
        The Readiness Gauge is especially advantageous for an organisation beginning its ESG
        reporting journey. It’s a simple, transparent tool that estimates the potential rating an
        independent agency might assign, based on your current ESG reporting compliance.
        <br />
        <br />
        The main benefits are:
        <br />
        <br />
        • Identifying areas that require attention
        <br />
        • Facilitating a structured approach to address gaps
        <br />
        • Highlighting both quick wins and long-term processes
        <br />
        • Supporting prioritisation and planning of ESG activities
        <br />• Providing guidelines for comprehensive and transparent stakeholder reporting.
      </>
    )
  },
  {
    question: `What kind of data can be uploaded or sourced from our organisation’s ERP (Enterprise Resource Planning) to make it auditable?`,
    answer: (
      <>
        Syntiro allows users to upload energy data in a wide range of data types to ensure audit
        readiness. These include:
        <br />
        <br />
        • Measured energy usage data
        <br />
        • Billing information (PDFs)
        <br />
        • Allocated emissions already converted with relevant emissions factors
        <br />
        • Supply chain data relevant to Scope 3 emissions and Science Based Targets initiative
        (SBTi) reporting.
        <br />
        <br />
        The platform aggregates Scope 3 data into the 15 different Scope 3 categories, and converts
        all supplied metrics to GHG emissions, using both the relevant local South African
        legislation and conversion factors, as well as the internationally recognised conversion
        factors. Syntiro also includes an exportable conversion table for audit and reference
        purposes, and aligns with relevant frameworks such as:
        <br />
        <br />
        Global Reporting Initiative (GRI)
        <br />
        GHG Protocol
        <br />
        UN SDGs
        <br />
        UN Global Compact, and others.
      </>
    )
  }
];

/**
 * Frequently Asked Questions view for all users.
 * @returns {JSX.Element}
 */
const FAQPage = () => {
  const [expandedFAQ, setExpandedFAQ] = React.useState<number | null>(null);
  const navigate: NavigateFunction = useNavigate();
  const mobile_viewport: boolean = useMediaQuery('(max-width:600px)');

  const handleContactNavigation = (): void => {
    navigate('/contact');
  };

  // Control which child collapse card is expanded.
  const handleChangeExpanded = (expanded: boolean, i: number): void => {
    if (!expanded && i === expandedFAQ) {
      setExpandedFAQ(null);
      return;
    }
    setExpandedFAQ(i);
  };

  return (
    <Grid container spacing={2} sx={{ my: 6, px: { xs: 0, md: 4, lg: 12 } }}>
      <Grid size={12}>
        {mobile_viewport ? (
          <Heading weight="Bold">Frequently Asked Questions</Heading>
        ) : (
          <Subtitle weight="Bold">Frequently Asked Questions</Subtitle>
        )}
      </Grid>
      <Grid size={12}>
        <Grid container spacing={0} sx={{ textAlign: 'left', my: 6 }}>
          {faqs.map((faq: FAQ, i: number) => (
            <Grid size={12} key={i}>
              <CollapseCard
                title={faq.question}
                flat={true}
                expanded={expandedFAQ === i}
                handleChangeExpanded={(expanded: boolean) => handleChangeExpanded(expanded, i)}
              >
                <CaptionText weight="Medium">{faq.answer}</CaptionText>
              </CollapseCard>
              {i < faqs.length - 1 && <PrimaryDivider opacity={0.6} sx={{ my: 2 }} />}
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid
        size={12}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          textAlign: 'left',
          gap: 2
        }}
      >
        <MinorHeading weight="Bold">{`Not Finding What You're Looking For?`}</MinorHeading>
        <PrimaryButton onClick={handleContactNavigation} sx={{ width: 150, height: 35 }}>
          Contact Us
        </PrimaryButton>
      </Grid>
    </Grid>
  );
};

export default FAQPage;
